<template>
  <div class="recipient-account">
    <div class="columns">
      <div class="column">
        <div class="group">
          <label>{{$t('payment_settings.processor')}}</label>
          <select v-model="processors.selected" v-if="isSelectedProcessorAvailableForContextCountry">
            <option :value="undefined" hidden disabled>{{$t('choose')}}...</option>
            <option :value="p.id" v-for="p of availableProcessors" :key="`pro_${p.id}`">
              {{$t('payment_settings.with')}} {{p.name}}
            </option>
          </select>
          <input type="text" readonly v-if="!isSelectedProcessorAvailableForContextCountry"
                 :value="`${selectedProcessorConfig.name} (${$t('payment_settings.not_available').replace('{COUNTRY}', contextCountry)})`">
        </div>
      </div>
      <div class="column">
        <div class="columns">
          <div class="column">
            <div class="group" v-if="canActivate && hasProcessorSelect">
              <label>{{$t('activated')}}</label>
              <select v-model="activated">
                <option :value="true" :disabled="!isSelectedProcessorAvailableForContextCountry">{{$t('yes')}}</option>
                <option :value="false">{{$t('no')}}</option>
              </select>
            </div>
          </div>
          <div class="column">
            <button class="btn btn-primary remove" v-if="canRemove" @click="remove">{{$t('remove')}}</button>
          </div>
        </div>
      </div>
    </div>
    <div class="subtitle" v-if="processors.selected" :class="{locked: !isSelectedProcessorAvailableForContextCountry}">
      {{$t('configuration')}}
    </div>
    <div class="columns" v-if="processors.selected" :class="{locked: !isSelectedProcessorAvailableForContextCountry}">
      <!-- Configs by processor -->
      <div class="column">

        <!-- Paysafe -->
        <template v-if="processors.selected === 'paysafe'">
          <div class="group">
            <label>Api Username</label>
            <input type="text" v-model="credentials.paysafe.apiUsername">
          </div>
          <div class="group">
            <label>Api Password</label>
            <input type="text" v-model="credentials.paysafe.apiPassword">
          </div>
          <div class="group">
            <label>Account number</label>
            <input type="text" v-model="credentials.paysafe.apiAccountNumber">
          </div>
          <div class="subtitle">Frontend</div>
          <div class="group">
            <input id="enableAVSCheckPaysafe" type="checkbox" v-model="credentials.paysafe.enableAVSCheck">
            <label for="enableAVSCheckPaysafe">{{$t('payment_settings.enable_avs_check')}}</label>
          </div>
          <div class="group">
            <label>Single Use Username</label>
            <input type="text" v-model="credentials.paysafe.singleUseUsername">
          </div>
          <div class="group">
            <label>Single Use Password</label>
            <input type="text" v-model="credentials.paysafe.singleUsePassword">
          </div>
        </template>

        <!-- Payfacto -->
        <template v-else-if="processors.selected === 'payfacto'">
          <div class="group">
            <label>Api key</label>
            <input type="text" v-model="credentials.payfacto.apiKey">
          </div>
          <div class="group">
            <label>Company Number</label>
            <input type="text" v-model="credentials.payfacto.companyNumber">
          </div>
          <div class="group">
            <label>Merchant Number (MID) - Online transactions</label>
            <input type="text" v-model="credentials.payfacto.merchantNumbers.I">
          </div>
          <div class="group">
            <label>Merchant Number (MID) - QR Payment transactions</label>
            <input type="text" v-model="credentials.payfacto.merchantNumbers.I_qrPay">
          </div>
          <div class="group">
            <label>Merchant Number (MID) - Terminal/Pinpad transactions</label>
            <input type="text" v-model="credentials.payfacto.merchantNumbers.N">
          </div>
          <div class="group">
            <label>Terminal flow</label>
            <select v-model="credentials.payfacto.terminalFlow">
              <option :value="undefined"></option>
              <option value="preAuthAndCompletion">PreAuthorization + Completion (default)</option>
              <option value="captureOnAuthNoRollback">Capture only, before POS notification</option>
            </select>
          </div>
          <div class="group">
            <label>Init Session lib (SuperAdmin only)</label>
            <select v-model="credentials.payfacto.initLib" :disabled="!isSuperAdmin">
              <option value="SHC">Secure Hosted Checkout</option>
              <option value="SecureFields">SecureFields</option>
            </select>
          </div>
          <Panel title="Endpoints (SecureFields only)" collapsed collapsible tag="superadmin" v-if="credentials.payfacto.initLib !== 'SHC'">
            <div class="group tag">
              <label><Tag>production</Tag>API URL</label>
              <input type="text" v-model="credentials.payfacto.prod.api">
            </div>
            <div class="group tag">
              <label><Tag>production</Tag>CSS Stylesheet URL</label>
              <input type="text" v-model="credentials.payfacto.prod.css">
            </div>
            <div class="group tag">
              <label><Tag>production</Tag>JS Script URL</label>
              <input type="text" v-model="credentials.payfacto.prod.js">
            </div>
            <div class="group tag">
              <label><Tag>production</Tag>SecureFields iframe URL</label>
              <input type="text" v-model="credentials.payfacto.prod.iframe">
            </div>
            <hr class="endpoint-hr">
            <div class="group tag">
              <label><Tag grey>development</Tag>API URL</label>
              <input type="text" v-model="credentials.payfacto.dev.api">
            </div>
            <div class="group tag">
              <label><Tag grey>development</Tag>CSS Stylesheet URL</label>
              <input type="text" v-model="credentials.payfacto.dev.css">
            </div>
            <div class="group tag">
              <label><Tag grey>development</Tag>JS Script URL</label>
              <input type="text" v-model="credentials.payfacto.dev.js">
            </div>
            <div class="group tag">
              <label><Tag grey>development</Tag>SecureFields iframe URL</label>
              <input type="text" v-model="credentials.payfacto.dev.iframe">
            </div>
          </Panel>
        </template>

        <!-- Cybersource -->
        <template v-else-if="processors.selected === 'cybersource'">
          <div class="group">
            <label>Merchant id</label>
            <input type="text" v-model="credentials.cybersource.merchantId">
          </div>
          <div class="group">
            <label>Key id</label>
            <input type="text" v-model="credentials.cybersource.keyId">
          </div>
          <div class="group">
            <label>Shared Key</label>
            <input type="text" v-model="credentials.cybersource.sharedSecretKey">
          </div>
          <div class="group">
            <label>Target Origin</label>
            <input type="text" v-model="credentials.cybersource.targetOrigin">
          </div>
        </template>

        <!-- Global -->
        <template v-else-if="processors.selected === 'global'">
          <div class="group">
            <label>Merchant ID</label>
            <input type="text" v-model="credentials.global.merchantId">
          </div>
          <div class="group">
            <label>Account ID</label>
            <input type="text" v-model="credentials.global.accountId">
          </div>
          <div class="group">
            <label>Shared secret</label>
            <input type="text" v-model="credentials.global.sharedSecret">
          </div>
        </template>

        <!-- ITC -->
        <template v-else-if="processors.selected === 'itc'">
          <div class="group">
            <label>Device ID</label>
            <input type="text" v-model="credentials.itc.deviceId">
          </div>
          <div class="group">
            <label>Vendor ID</label>
            <input type="text" v-model="credentials.itc.vendorId">
          </div>
          <div class="group">
            <label>API Endpoint (URL)</label>
            <input type="text" v-model="credentials.itc.apiEndpoint">
          </div>
        </template>

        <!-- VALITOR -->
        <template v-else-if="processors.selected === 'valitor'">
          <div class="group info-text" v-show="!liveMode">
            <div>In test mode, parameters are hardcoded to...</div>
            <div>Verification Code = '12345'</div>
            <div>Merchant ID = '1'</div>
          </div>
          <div class="group">
            <label>Verification Code</label>
            <input type="text" v-model="credentials.valitor.verificationCode" :disabled="!liveMode">
          </div>
          <div class="group">
            <label>Merchant ID</label>
            <input type="text" v-model="credentials.valitor.merchantId" :disabled="!liveMode">
          </div>

          <br>
          <label><b>Credit cards logos to display (not synced with merchant configuration)</b></label>
          <div class="group cards">
            <div class="card">
              <input id="valitorCardVisa" type="checkbox" v-model="credentials.valitor.cards.visa">
              <label for="valitorCardVisa">Visa</label>
            </div>
            <div class="card">
              <input id="valitorCardMastercard" type="checkbox" v-model="credentials.valitor.cards.mastercard">
              <label for="valitorCardMastercard">MasterCard</label>
            </div>
            <div class="card">
              <input id="valitorCardApplePay" type="checkbox" v-model="credentials.valitor.cards.applePay">
              <label for="valitorCardApplePay">Apple Pay</label>
            </div>
            <div class="card">
              <input id="valitorCardAmex" type="checkbox" v-model="credentials.valitor.cards.amex">
              <label for="valitorCardAmex">American Express</label>
            </div>
          </div>
        </template>

        <!-- NMI -->
        <template v-else-if="processors.selected === 'nmi'">
          <div class="group">
            <label>Security Key</label>
            <input type="text" v-model="credentials.nmi.securityKey">
          </div>
          <div class="group">
            <label>Tokenization Key</label>
            <input type="text" v-model="credentials.nmi.tokenizationKey">
          </div>
          <div class="group">
            <label>Checkout Key</label>
            <input type="text" v-model="credentials.nmi.checkoutKey">
          </div>
          <div class="group">
            <input id="nmi_kount" type="checkbox" v-model="credentials.nmi.enableKount">
            <label for="nmi_kount">Enable Kount</label>
          </div>
        </template>

      </div>

      <!-- More options -->
      <div class="column">
        <template v-if="hasProcessorSelect">
          <div class="group" v-if="!mustHaveLiveMode">
            <input :id="'chkLiveMode' + uuid" type="checkbox" v-model="liveMode" >
            <label :for="'chkLiveMode' + uuid">LiveMode?</label>
          </div>
          <div class="group" v-if="processorCanSaveCard">
            <input :id="'chkRegisterCard' + uuid" type="checkbox" v-model="saveEnabled">
            <label :for="'chkRegisterCard' + uuid">{{$t('payment_settings.enable_save_card')}}</label>
          </div>
          <div class="group" v-if="processorHas3DSecure">
            <input :id="'chk3ds' + uuid" type="checkbox" v-model="enable3DsProxy">
            <label :for="'chk3ds' + uuid">{{$t('payment_settings.enable_3d_secure')}}</label>
          </div>
          <div class="group" v-if="processorHasApplePay">
            <input :id="'chkApplePay' + uuid" type="checkbox" v-model="applePayEnabled">
            <label :for="'chkApplePay' + uuid">{{$t('payment_settings.enable_apple_pay')}}</label>
          </div>
          <div class="group" v-if="processorHasGooglePay">
            <input :id="'chkGooglePay' + uuid" type="checkbox" v-model="googlePayEnabled">
            <label :for="'chkGooglePay' + uuid">{{ $t('payment_settings.enable_google_pay') }}</label>
          </div>
          <div class="group not-supported" v-if="!processorCanSaveCard">Card registration not supported</div>
          <div class="group not-supported" v-if="!processorHas3DSecure">3DS not supported</div>
          <div class="group not-supported" v-if="!processorHasApplePay">Apple Pay not supported</div>
          <div class="group not-supported" v-if="!processorHasGooglePay">{{ $t('payment_settings.google_pay_not_supported') }}</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Tag from "@/components/tools/Tag";

export default {
  name: "RecipientAccount",

  components: { Tag },

  props: {
    recipientAccount: Object,
    canActivate: {
      type: Boolean,
      default: false,
    },
    canRemove: {
      type: Boolean,
      default: false,
    },
    exceptProcessors: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      uuid: null,
      processors: {
        selected: null,
        all: [
          {id: "payfacto", name: "PayFacto", countries: ["CA"]},
          {id: "paysafe", name: "Paysafe", countries: ["CA"]},
          {id: "cybersource", name: "Cybersource", countries: ["US"]},
          {id: "global", name: "Global", countries: ["CA"]},
          {id: "itc", name: "ITC", countries: ["US"]},
          {id: "valitor", name: "Valitor", countries: ["GB"]},
          {id: "nmi", name: "NMI", countries: ["CA","US"]},
        ]
      },
      credentials: {},
      activated: true,
      liveMode: this.mustHaveLiveMode,
      saveEnabled: false,
      applePayEnabled: false,
      googlePayEnabled: false,
    }
  },

  mounted() {
    this.uuid = window.generateUUID();
  },

  computed: {
    availableProcessors() {
      return this.processors.all.filter(p => (p.id === this.processors.selected || !this.exceptProcessors.includes(p.id))
          && this.isProcessorAvailableForContextCountry(p))
    },
    processorCanSaveCard() {
      return ['payfacto', 'itc', 'cybersource', 'nmi'].includes(this.processors.selected)
    },
    processorHas3DSecure() {
      return ['payfacto', 'paysafe', 'nmi'].includes(this.processors.selected)
    },
    processorHasApplePay() {
      return ['payfacto', 'nmi'].includes(this.processors.selected)
    },
    processorHasGooglePay() {
      return ['nmi'].includes(this.processors.selected)
    },
    hasProcessorSelect() {
      return !!this.processors.selected
    },
    selectedProcessorConfig() {
      return this.processors.all.find(p => p.id === this.processors.selected);
    },
    branch() {
      return this.$store.state.contextBranch;
    },
    company() {
      return this.$store.state.contextCompany;
    },
    contextCountry() {
      return this.company ? this.company.country : "";
    },
    isSelectedProcessorAvailableForContextCountry() {
      return this.isProcessorAvailableForContextCountry(this.selectedProcessorConfig);
    },
    mustHaveLiveMode() {
      let config = Parameters.paymentTestModeAllowedBranches;
      if (Array.isArray(config) && config.length > 0 && this.branch) {
        return config.indexOf(this.branch.nameCanonical) === -1;
      }
      return false;
    },
    isSuperAdmin() {
      return this.$store.getters.loggedUserHasSuperAdmin;
    },
    enable3DsProxy: {
      get() {
        return this.credentials[this.processors.selected].enable3DSecure;
      },
      set(enabled) {
        this.credentials[this.processors.selected].enable3DSecure = enabled;
      }
    }
  },

  watch: {
    recipientAccount: {
      immediate: true,
      handler(val) {
        let account = val ? JSON.parse(JSON.stringify(val)) : {};
        if (!account.credentials) {
          account.credentials = {}
        }
        this.processors.selected = account.type;
        this.liveMode = this.mustHaveLiveMode ? true : (account.liveMode || false);
        this.saveEnabled = account.saveEnabled || false;
        this.applePayEnabled = account.applePayEnabled || false;
        this.googlePayEnabled = account.googlePayEnabled || false;
        this.activated = typeof account.activated === 'boolean' ? account.activated : true;
        this.credentials = {
          paysafe: {
            apiUsername: account.credentials.apiUsername || "",
            apiPassword: account.credentials.apiPassword || "",
            apiAccountNumber: account.credentials.apiAccountNumber || "",
            enable3DSecure: account.credentials.enable3DSecure || false,
            enableAVSCheck: account.credentials.enableAVSCheck || false,
            singleUseUsername: account.credentials.singleUseUsername || "",
            singleUsePassword: account.credentials.singleUsePassword || "",
          },
          payfacto: {
            apiKey: account.credentials.apiKey || "",
            companyNumber: account.credentials.companyNumber || "",
            merchantNumbers: {
              I: account.credentials.merchantNumbers ? account.credentials.merchantNumbers.I : account.credentials.merchantNumber || "",
              I_qrPay: account.credentials.merchantNumbers ? (account.credentials.merchantNumbers.I_qrPay || "") : "",
              N: account.credentials.merchantNumbers ? account.credentials.merchantNumbers.N : ""
            },
            terminalFlow: account.credentials.terminalFlow,
            enable3DSecure: account.credentials.enable3DSecure || false,
            initLib: account.credentials.initLib || null,
            dev: {
              api: account.credentials.dev ? account.credentials.dev.api : "",
              css: account.credentials.dev ? account.credentials.dev.css : "",
              iframe: account.credentials.dev ? account.credentials.dev.iframe : "",
              js: account.credentials.dev ? account.credentials.dev.js : ""
            },
            prod: {
              api: account.credentials.prod ? account.credentials.prod.api : "",
              css: account.credentials.prod ? account.credentials.prod.css : "",
              iframe: account.credentials.prod ? account.credentials.prod.iframe : "",
              js: account.credentials.prod ? account.credentials.prod.js : ""
            }
          },
          nmi: {
            securityKey: account.credentials.securityKey || "",
            tokenizationKey: account.credentials.tokenizationKey || "",
            checkoutKey: account.credentials.checkoutKey || "",
            enable3DSecure: account.credentials.enable3DSecure || false,
            enableKount: account.credentials.enableKount || false,
          },
          cybersource: {
            merchantId: account.credentials.merchantId || "",
            keyId: account.credentials.keyId || "",
            sharedSecretKey: account.credentials.sharedSecretKey || "",
            targetOrigin: account.credentials.targetOrigin || ""
          },
          global: {
            merchantId: account.credentials.merchantId || "",
            accountId: account.credentials.accountId || "",
            sharedSecret: account.credentials.sharedSecret || "",
          },
          itc: {
            deviceId: account.credentials.deviceId || "",
            vendorId: account.credentials.vendorId || "",
            apiEndpoint: account.credentials.apiEndpoint || ""
          },
          valitor: {
            verificationCode: account.credentials.verificationCode || "",
            merchantId: account.credentials.merchantId || "",
            cards: {
              mastercard: account.credentials.cards ? account.credentials.cards.mastercard : true,
              visa: account.credentials.cards ? account.credentials.cards.visa : true,
              applePay: account.credentials.cards ? account.credentials.cards.applePay : false,
              amex: account.credentials.cards ? account.credentials.cards.amex : false
            }
          }
        }
      }
    }
  },

  methods: {
    remove() {
      this.$emit('remove');
    },
    isActivated() {
      return this.activated;
    },
    isProcessorAvailableForContextCountry(processor) {
      if (!processor) {
        return true;
      }
      return !this.contextCountry || processor.countries.indexOf(this.contextCountry) > -1;
    },
    getData() {
      if (!this.processors.selected) {
        return null;
      }
      let selected = this.processors.selected;
      let selectedCredentials = this.credentials[selected];
      if (!selectedCredentials) {
        return null;
      }
      let credentials = JSON.parse(JSON.stringify(selectedCredentials));
      /* remove endpoints when initLib is not legacy */
      if (selected === 'payfacto' && credentials.initLib === 'SHC') {
        credentials.dev = undefined;
        credentials.prod = undefined;
      }
      return {
        liveMode: this.liveMode,
        saveEnabled: this.processorCanSaveCard && this.saveEnabled,
        applePayEnabled: this.processorHasApplePay && this.applePayEnabled,
        googlePayEnabled: this.processorHasGooglePay && this.googlePayEnabled,
        type: this.processors.selected,
        credentials
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .not-supported {
    font-size: 14px;
    font-style: italic;
    opacity: 0.9;
  }
  .info-text {
    font-size: 14px;
    font-style: italic;
    opacity: 0.9;
    color: cornflowerblue;
    margin-bottom: 20px;
  }
  .group.cards {
    padding-top: 10px;
  }
  .remove {
    margin-top: 20px;
    float: right;
  }
  .subtitle {
    padding-top: 15px;
  }

  .endpoint-hr {
    margin: 20px 0;
    border-top: 0;
    border-bottom: 1px dashed #ccc;
  }

  .group.tag {
    label {
      justify-content: flex-start;

      .tag {
        margin-right: 10px;
      }
    }
  }
</style>
